import { DashboardLayout } from "../../components/dashboard-layout";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../theme";
import axios from "axios";
import { t } from "i18next";
import { 
    Box, 
    Container, 
    Typography, 
    Card, 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableRow, 
    Checkbox, 
    Button,
    CircularProgress,
    Alert,
    Snackbar
} from "@mui/material";
import { useEffect, useState } from "react";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";

// Danh sách các quyền hạn khả dụng trong hệ thống
const AVAILABLE_PERMISSIONS = [
    { key: "dashboard.view", label: "Xem thống kê (Dashboard)" },
    { key: "users.view", label: "Xem danh sách thành viên" },
    { key: "users.edit", label: "Chỉnh sửa thành viên (cộng tiền/khóa)" },
    { key: "transactions.view", label: "Xem lịch sử nạp/rút" },
    { key: "transactions.approve", label: "Duyệt lệnh nạp/rút tiền" },
    { key: "banks.view", label: "Xem ngân hàng hệ thống" },
    { key: "banks.manage", label: "Quản lý ngân hàng (Thêm/Sửa)" },
    { key: "games.view", label: "Xem tỷ lệ & kết quả game" },
    { key: "games.config.update", label: "Can thiệp chỉnh cầu/kết quả game" },
    { key: "finance.view", label: "Xem báo cáo doanh thu" },
    { key: "audit.view", label: "Xem nhật ký hoạt động (Audit Logs)" },
    { key: "settings.manage", label: "Cài đặt hệ thống (Config/Tỷ lệ)" },
    { key: "rbac.manage", label: "Quản lý phân quyền (RBAC)" }
];

function Permissions() {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

    axios.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem("user");
            if (token) {
                config.headers["Authorization"] = `Bearer ${token}`;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    const fetchPermissions = () => {
        setLoading(true);
        axios
            .get(`${process.env.REACT_APP_API_URL}/admin-sec/permissions/matrix`)
            .then((res) => {
                if (res.data.success) {
                    setRoles(res.data.data);
                }
            })
            .catch((err) => {
                console.error(err);
                setToast({ open: true, message: "Lỗi tải ma trận phân quyền!", severity: "error" });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchPermissions();
    }, []);

    const handleCheckboxChange = (roleName, permissionKey, checked) => {
        setRoles(prevRoles => prevRoles.map(role => {
            if (role.role === roleName) {
                const updatedPermissions = checked
                    ? [...role.permissions, permissionKey]
                    : role.permissions.filter(p => p !== permissionKey);
                return { ...role, permissions: updatedPermissions };
            }
            return role;
        }));
    };

    const handleSave = (roleObj) => {
        setSaving(true);
        axios
            .put(`${process.env.REACT_APP_API_URL}/admin-sec/permissions/matrix`, {
                role: roleObj.role,
                permissions: roleObj.permissions
            })
            .then((res) => {
                if (res.data.success) {
                    setToast({ open: true, message: `Lưu phân quyền vai trò "${roleObj.role}" thành công!`, severity: "success" });
                } else {
                    setToast({ open: true, message: res.data.message || "Không thể lưu!", severity: "error" });
                }
            })
            .catch((err) => {
                console.error(err);
                setToast({ open: true, message: "Lỗi lưu phân quyền!", severity: "error" });
            })
            .finally(() => {
                setSaving(false);
            });
    };

    return (
        <ThemeProvider theme={theme}>
            <DashboardLayout>
                <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <Container maxWidth="lg">
                        <Typography className="source-page-title" variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                            <SecurityRoundedIcon sx={{ mr: 1, verticalAlign: "middle", color: "#2563eb" }} /> Phân Quyền Vai Trò (RBAC)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                            Chỉ Super Admin mới được thay đổi và thiết lập quyền truy cập cho nhóm Admin và Nhân viên (Staff).
                        </Typography>

                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            roles.map((roleObj) => (
                                <Card key={roleObj.role} sx={{ p: 3, mb: 4 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase", color: "primary.main" }}>
                                            Nhóm vai trò: {roleObj.role === "super_admin" ? "Super Admin (Cấp cao nhất)" : "Admin/Nhân viên (Cấp dưới)"}
                                        </Typography>
                                        {roleObj.role !== "super_admin" && (
                                            <Button 
                                                variant="contained" 
                                                size="small" 
                                                onClick={() => handleSave(roleObj)}
                                                disabled={saving}
                                            >
                                                Lưu thay đổi
                                            </Button>
                                        )}
                                    </Box>

                                    <Table size="small">
                                        <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: "bold" }}>Quyền hạn</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: 150 }} align="center">Trạng thái cấp quyền</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {AVAILABLE_PERMISSIONS.map((perm) => {
                                                const hasPermission = roleObj.permissions.includes(perm.key);
                                                return (
                                                    <TableRow key={perm.key} hover>
                                                        <TableCell sx={{ py: 1 }}>{perm.label} (<code>{perm.key}</code>)</TableCell>
                                                        <TableCell align="center" sx={{ py: 1 }}>
                                                            <Checkbox
                                                                checked={hasPermission}
                                                                disabled={roleObj.role === "super_admin"} // Khóa cứng super admin full quyền
                                                                onChange={(e) => handleCheckboxChange(roleObj.role, perm.key, e.target.checked)}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </Card>
                            ))
                        )}
                    </Container>
                </Box>
                <Snackbar 
                    open={toast.open} 
                    autoHideDuration={4000} 
                    onClose={() => setToast({ ...toast, open: false })}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                    <Alert severity={toast.severity} sx={{ width: '100%' }}>
                        {toast.message}
                    </Alert>
                </Snackbar>
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default Permissions;
